<script lang="ts">
  import { storyblokEditable } from '@storyblok/svelte';
  import { PageSection } from '@athena-lib/Page';
  import { BaseHeading, type HeadingSize, HeadingXSmall } from '@athena-lib/Text';
  import { Stack, StackItem } from '@athena-lib/Stack';
  import { Image } from '@athena-lib/Image';
  import { Column, Columns } from '@athena-lib/Columns';
  import { Accordion, AccordionItem } from '@athena-lib/Accordion';
  import clsx from 'clsx';
  import { parseHeadingLevel } from '@athena-lib/Text/BaseHeading/types';
  import { RichText } from '../RichText';
  import { isRichTextEmpty } from '../RichText/types';
  import type { ExpandedFaqPageSectionBlock } from '$lib/server/expanders/faqPageSection';
  const { blok }: { blok: ExpandedFaqPageSectionBlock } = $props();

  const imageWidthClasses = {
    large: 'w-full',
    medium: 'w-[75%]',
    small: 'w-[50%]',
  };
</script>

<PageSection
  id={blok.elementId}
  action={storyblokEditable}
  actionArg={blok}
  ySpace={{ top: blok.topPadding ?? 'medium', bottom: blok.bottomPadding ?? 'medium' }}
>
  <Columns collapseBelow="md" space="large">
    <Column width="2/5">
      <Stack space="gutter">
        {#if blok.headingText}
          <StackItem>
            <BaseHeading
              level={parseHeadingLevel(blok.headingTag) ?? 2}
              size={(blok.headingSize as HeadingSize) ?? 'medium'}
            >
              {blok.headingText}
            </BaseHeading>
          </StackItem>
        {/if}
        {#if !isRichTextEmpty(blok.description)}
          <StackItem class="max-w-text-max">
            <RichText text={blok.description} />
          </StackItem>
        {/if}
        {#if blok.image?.filename}
          <StackItem class="hidden md:block">
            <div class="flex justify-center pt-gutter">
              <Image
                src={blok.image.filename}
                alt={blok.image.alt ?? blok.image.filename}
                class={clsx(imageWidthClasses[blok.imageSize || 'large'])}
              />
            </div>
          </StackItem>
        {/if}
      </Stack>
    </Column>
    <Column width="3/5">
      {#if blok.useDesktopAccordion || blok.useMobileAccordion}
        <div
          class={clsx(
            !blok.useMobileAccordion && 'hidden',
            blok.useDesktopAccordion ? 'md:block' : 'md:hidden',
            'md:mt-[4px]', // Needed to visually align the top of the accordion with the heading
          )}
        >
          <Accordion space="medium">
            {#each blok.expandedQuestions ?? [] as question}
              <AccordionItem heading={question.question ?? ''}>
                <RichText text={question.answer} />
              </AccordionItem>
            {/each}
          </Accordion>
        </div>
      {/if}
      {#if !blok.useDesktopAccordion || !blok.useMobileAccordion}
        <div
          class={clsx(
            blok.useMobileAccordion && 'hidden',
            blok.useDesktopAccordion ? 'md:hidden' : 'md:block',
            'md:mt-[4px]', // Needed to visually align the top of the questions with the heading
          )}
        >
          <Stack space="xlarge">
            {#each blok.expandedQuestions ?? [] as question}
              <StackItem>
                <Stack space="gutter">
                  <StackItem>
                    <HeadingXSmall size={{ sm: 'small' }}>{question.question}</HeadingXSmall>
                  </StackItem>
                  <StackItem>
                    <RichText text={question.answer} />
                  </StackItem>
                </Stack>
              </StackItem>
            {/each}
          </Stack>
        </div>
      {/if}
    </Column>
  </Columns>
</PageSection>
